/**
 * Agreement Types Tab
 *
 * Manage agreement types for evaluations (afspraak categorieën).
 */

import { useState } from 'react';
import {
  useAgreementTypes,
  useCreateAgreementType,
  useUpdateAgreementType,
  useDeleteAgreementType,
} from '../useSettings';
import type {
  AgreementTypeDTO,
  CreateAgreementTypeRequest,
  UpdateAgreementTypeRequest,
} from '@functieloonhuis/shared';

// Available icons for agreement types
const AVAILABLE_ICONS = [
  { name: 'lightbulb', icon: '💡', label: 'Idee/Ontwikkeling' },
  { name: 'book', icon: '📚', label: 'Training/Opleiding' },
  { name: 'target', icon: '🎯', label: 'Doel/Target' },
  { name: 'star', icon: '⭐', label: 'Prestatie' },
  { name: 'users', icon: '👥', label: 'Samenwerking' },
  { name: 'clock', icon: '⏰', label: 'Deadline' },
  { name: 'check', icon: '✅', label: 'Actie' },
  { name: 'chart', icon: '📈', label: 'Groei' },
];

// Available colors for agreement types
const AVAILABLE_COLORS = [
  { name: 'blue', hex: '#3B82F6', label: 'Blauw' },
  { name: 'green', hex: '#10B981', label: 'Groen' },
  { name: 'yellow', hex: '#F59E0B', label: 'Geel' },
  { name: 'red', hex: '#EF4444', label: 'Rood' },
  { name: 'purple', hex: '#8B5CF6', label: 'Paars' },
  { name: 'pink', hex: '#EC4899', label: 'Roze' },
  { name: 'indigo', hex: '#6366F1', label: 'Indigo' },
  { name: 'gray', hex: '#6B7280', label: 'Grijs' },
];

interface FormData {
  name: string;
  displayName: string;
  description: string;
  icon: string;
  color: string;
}

const defaultFormData: FormData = {
  name: '',
  displayName: '',
  description: '',
  icon: 'lightbulb',
  color: 'blue',
};

export function AgreementTypesTab() {
  const { data: types, isLoading, error } = useAgreementTypes();
  const createType = useCreateAgreementType();
  const updateType = useUpdateAgreementType();
  const deleteType = useDeleteAgreementType();

  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const handleStartAdd = () => {
    setFormData(defaultFormData);
    setIsAddingNew(true);
    setEditingId(null);
  };

  const handleStartEdit = (type: AgreementTypeDTO) => {
    setFormData({
      name: type.name,
      displayName: type.displayName,
      description: type.description || '',
      icon: type.icon || 'lightbulb',
      color: type.color || 'blue',
    });
    setEditingId(type.id);
    setIsAddingNew(false);
  };

  const handleCancel = () => {
    setIsAddingNew(false);
    setEditingId(null);
    setFormData(defaultFormData);
  };

  const handleSave = async () => {
    if (isAddingNew) {
      const data: CreateAgreementTypeRequest = {
        name: formData.name,
        displayName: formData.displayName,
        description: formData.description || undefined,
        icon: formData.icon || undefined,
        color: formData.color || undefined,
      };
      await createType.mutateAsync(data);
    } else if (editingId) {
      const data: UpdateAgreementTypeRequest = {
        displayName: formData.displayName,
        description: formData.description || undefined,
        icon: formData.icon || undefined,
        color: formData.color || undefined,
      };
      await updateType.mutateAsync({ id: editingId, data });
    }
    handleCancel();
  };

  const handleToggleActive = async (type: AgreementTypeDTO) => {
    await updateType.mutateAsync({
      id: type.id,
      data: { isActive: !type.isActive },
    });
  };

  const handleDelete = async (id: number) => {
    await deleteType.mutateAsync(id);
    setDeleteConfirm(null);
  };

  const getIconEmoji = (iconName: string | null) => {
    const found = AVAILABLE_ICONS.find((i) => i.name === iconName);
    return found?.icon || '📋';
  };

  const getColorHex = (colorName: string | null) => {
    const found = AVAILABLE_COLORS.find((c) => c.name === colorName);
    return found?.hex || '#6B7280';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Laden...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-700">Fout bij het laden van afspraaktypes.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Afspraaktypes</h2>
          <p className="text-sm text-gray-500">
            Beheer categorieën voor afspraken in beoordelingen
          </p>
        </div>
        {!isAddingNew && !editingId && (
          <button
            onClick={handleStartAdd}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nieuw type
          </button>
        )}
      </div>

      {/* Info Banner */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <svg className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-blue-700">
            Afspraaktypes worden gebruikt om afspraken te categoriseren tijdens beoordelingsgesprekken (bijv. ontwikkeldoelen, trainingen, targets).
          </p>
        </div>
      </div>

      {/* Add/Edit Form */}
      {(isAddingNew || editingId !== null) && (
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h3 className="text-md font-medium text-gray-900 mb-4">
            {isAddingNew ? 'Nieuw afspraaktype toevoegen' : 'Afspraaktype bewerken'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {isAddingNew && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Technische naam *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="bijv. development"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">Alleen letters, geen spaties</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Weergavenaam *
              </label>
              <input
                type="text"
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                placeholder="bijv. Ontwikkeling"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Beschrijving
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Korte beschrijving van dit type afspraak..."
                rows={2}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Icoon
              </label>
              <div className="flex flex-wrap gap-2">
                {AVAILABLE_ICONS.map((icon) => (
                  <button
                    key={icon.name}
                    type="button"
                    onClick={() => setFormData({ ...formData, icon: icon.name })}
                    className={`p-2 rounded-md text-xl ${
                      formData.icon === icon.name
                        ? 'bg-blue-100 ring-2 ring-blue-500'
                        : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                    title={icon.label}
                  >
                    {icon.icon}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kleur
              </label>
              <div className="flex flex-wrap gap-2">
                {AVAILABLE_COLORS.map((color) => (
                  <button
                    key={color.name}
                    type="button"
                    onClick={() => setFormData({ ...formData, color: color.name })}
                    className={`w-8 h-8 rounded-full ${
                      formData.color === color.name
                        ? 'ring-2 ring-offset-2 ring-blue-500'
                        : ''
                    }`}
                    style={{ backgroundColor: color.hex }}
                    title={color.label}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm font-medium text-gray-700 mb-2">Voorbeeld:</p>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium text-white"
              style={{ backgroundColor: getColorHex(formData.color) }}
            >
              <span>{getIconEmoji(formData.icon)}</span>
              <span>{formData.displayName || 'Weergavenaam'}</span>
            </div>
          </div>

          {/* Form Actions */}
          <div className="mt-4 flex justify-end gap-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Annuleren
            </button>
            <button
              onClick={handleSave}
              disabled={createType.isPending || updateType.isPending || !formData.displayName || (isAddingNew && !formData.name)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {(createType.isPending || updateType.isPending) ? 'Opslaan...' : 'Opslaan'}
            </button>
          </div>
        </div>
      )}

      {/* Agreement Types List */}
      {types && types.length > 0 ? (
        <div className="overflow-hidden border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Beschrijving
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acties
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {types.map((type) => (
                <tr key={type.id} className={`hover:bg-gray-50 ${!type.isActive ? 'opacity-50' : ''}`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full text-white text-sm"
                        style={{ backgroundColor: getColorHex(type.color) }}
                      >
                        {getIconEmoji(type.icon)}
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">{type.displayName}</span>
                        <span className="block text-xs text-gray-500">{type.name}</span>
                      </div>
                      {type.isSystem && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          Systeem
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-500 truncate max-w-xs block">
                      {type.description || '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleActive(type)}
                      disabled={updateType.isPending}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        type.isActive
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {type.isActive ? 'Actief' : 'Inactief'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleStartEdit(type)}
                        className="text-blue-600 hover:text-blue-900"
                        disabled={editingId !== null || isAddingNew}
                      >
                        Bewerken
                      </button>
                      {!type.isSystem && (
                        <>
                          {deleteConfirm === type.id ? (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleDelete(type.id)}
                                disabled={deleteType.isPending}
                                className="text-red-600 hover:text-red-900"
                              >
                                Bevestigen
                              </button>
                              <button
                                onClick={() => setDeleteConfirm(null)}
                                className="text-gray-500 hover:text-gray-700"
                              >
                                Annuleer
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setDeleteConfirm(type.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Verwijderen
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">Geen afspraaktypes</h3>
          <p className="mt-2 text-sm text-gray-500">
            Voeg een nieuw afspraaktype toe om te beginnen.
          </p>
          <button
            onClick={handleStartAdd}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nieuw type
          </button>
        </div>
      )}
    </div>
  );
}
